import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar/index';
import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { Table, Modal, Tooltip, Button } from 'antd';
import qs from 'qs';
import _ from 'lodash';
import { checkPermission } from '@/utils/permission';
import { cleanObject, isValidDate, renderBooleanIcon, stripHtml } from '@/utils/helpers';
import HtmlParser from 'react-html-parser';
import TextTruncate from 'react-text-truncate';
import LaboForm from '@/routes/labos/LaboForm';
import { fetchLaboList, fetchLaboDetail, deleteLabos } from '@/redux/actions/laboActions';

const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/labos', breadcrumbName: 'Đối tác labo' },
];

export default function LaboList() {
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();

    const items = useSelector(state => state.laboReducer.list.items);
    const pagination = useSelector(state => state.laboReducer.list.pagination);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isOpenForm, setIsOpenForm] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);

    const routeName = 'labos';
    const allowCreate = checkPermission(routeName, 'create');
    const allowUpdate = checkPermission(routeName, 'update');
    const allowDelete = checkPermission(routeName, 'delete');

    const query = qs.parse(location.search.slice(1));

    const loadData = useCallback((q = query) => {
        setIsLoading(true);
        dispatch(fetchLaboList(q)).finally(() => setIsLoading(false));
    }, [dispatch, query]);

    useEffect(() => {
        loadData();
    }, [location.search]);

    const handleChangeTable = (pagination, sorter) => {
        let q = { ...query };
        if (!_.isEmpty(pagination)) {
            q.page = pagination.current;
            q.per_page = pagination.pageSize;
        }
        if (!_.isEmpty(sorter)) {
            if (sorter.order) {
                q.sort_field = sorter.field;
                q.sort_type = sorter.order === 'ascend' ? 'ASC' : 'DESC';
            } else {
                delete q.sort_field;
                delete q.sort_type;
            }
        }
        history.push({ pathname: location.pathname, search: qs.stringify(q) });
    };

    const handleEdit = async (id) => {
        const res = await dispatch(fetchLaboDetail(id));
        setCurrentItem(res);
        setIsOpenForm(true);
    };

    const handleRemove = () => {
        dispatch(deleteLabos(selectedRowKeys)).then(() => {
            setSelectedRowKeys([]);
            loadData();
            setTimeout(() => Modal.destroyAll(), 800);
        });
    };

    const handleFilterChange = (name, value) => {
        let q = { ...query, [name]: value };
        q = cleanObject(q);
        history.push({ pathname: location.pathname, search: qs.stringify(q) });
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', key: 'id', sorter: true, width: '50px' },
        {
            title: '#', align: 'center', width: '50px',
            render: (_, record) => (
                <Button size="small" type="dashed" className='btn-edit' icon={<EditOutlined />} onClick={() => handleEdit(record.id)} disabled={!allowUpdate}>Sửa</Button>
            )
        },
        { title: 'Tên labo', dataIndex: 'name', key: 'name' },
        { title: 'Điện thoại', dataIndex: 'phone', key: 'phone', width: '150px' },
        {
            title: 'Ghi chú', dataIndex: 'note', key: 'note', width: '200px',
            render: (text) => (
                <div style={{ wordBreak: "break-word" }}>
                    <Tooltip title={HtmlParser(text)} placement="topLeft">
                        <TextTruncate line={1} truncateText="…" text={stripHtml(text)} />
                    </Tooltip>
                </div>
            )
        },
        {
            title: 'Ngày tạo', dataIndex: 'created_at', key: 'created_at', sorter: true, width: '140px',
            render: (text) => (<div>{isValidDate(text, true)}</div>)
        },
        {
            title: 'Người tạo', dataIndex: 'created_user', key: 'created_user', width: '200px',
            render: (_, record) => (<div>{record?.created_user?.full_name}</div>)
        },
        {
            title: 'Trạng thái', dataIndex: 'status', key: 'status', align: 'center', width: '100px',
            render: (text) => <>{renderBooleanIcon(text)}</>
        }
    ];

    return (
        <div>
            <PageTitle routes={routes} title="Đối tác labo" />
            <TableActionBar
                isShowAddButton={allowCreate}
                isShowDeleteButton={allowDelete}
                onAdd={() => setIsOpenForm(true)}
                disabled={!selectedRowKeys.length}
                onDelete={handleRemove}
                showSearch
                showFilter={false}
                activeFilter={query.status}
                onSearch={value => handleFilterChange('keyword', value)}
                defaultKeyword={query.keyword}
            >
                <Button icon={<ReloadOutlined />} loading={isLoading} onClick={() => loadData()}>Refresh</Button>
                {selectedRowKeys.length > 0 && <span>Đang chọn {selectedRowKeys.length} bản ghi</span>}
            </TableActionBar>

            <Table
                rowKey='id'
                size='small'
                tableLayout="auto"
                columns={columns}
                rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                loading={isLoading}
                dataSource={items || []}
                pagination={{
                    pageSize: pagination.perPage,
                    current: pagination.currentPage,
                    total: pagination.total,
                    showTotal: total => `Tổng ${total} bản ghi`,
                    defaultPageSize: 30, showSizeChanger: true, pageSizeOptions: ['10', '20', '30', '40', '50', '100', '200']
                }}
                onChange={(pagination, filter, sorter) => handleChangeTable(pagination, sorter)}
                scroll={{ x: 'max-content' }}
            />

            <LaboForm
                visible={isOpenForm}
                currentItem={currentItem}
                onCancel={() => setIsOpenForm(false)}
                loadData={loadData}
            />
        </div>
    );
}